import { useCallback, useState } from 'react';
import { amsPublicApiTokensSwitchWorkspace } from 'service/amsApi';
import type { ErrorResponse } from 'connectors/ams';
import { showNotification } from 'common/function';
import { reportError } from 'utils/reporting';

type SwitchResult = 'switched' | 'refused';

type UseWorkspaceSwitchReturn = {
  switchWorkspace: (workspaceCode: string) => Promise<SwitchResult>;
  isSwitching: boolean;
};

const TARGET_UNAVAILABLE = 'That workspace is no longer available to you. Refresh to see the current list.';
const ACCOUNT_NOT_READY = 'Your account is not ready to switch workspaces. Contact support if this continues.';
const ACCOUNT_DENIED = "You can't switch workspaces on this account. Contact support if this continues.";
const GENERIC_FAILURE = 'Could not switch workspace. Please try again.';

/**
 * Maps a refusal to its message. Split out to keep the caller flat, and because each arm is a
 * distinct server contract rather than a variation on one.
 */
function refuse(error: ErrorResponse): SwitchResult {
  switch (error.code) {
    case 'not_found':
      // 12a collapses ungranted, deleted, PROVISIONING and never-existed into one 404, so this is
      // as specific as the server allows. Target-specific: another workspace may still work.
      showNotification(TARGET_UNAVAILABLE, 'Workspace unavailable', 'error');
      break;
    case 'account_not_provisioned':
      showNotification(ACCOUNT_NOT_READY, 'Account unavailable', 'error');
      break;
    case 'forbidden':
    case 'account_access_denied':
      // Account-wide, not target-specific: no workspace will work, so the copy must not invite the
      // user to try another one. `account_access_denied` means the caller's own user row is gone;
      // AMS already logs that server-side, so it is not re-reported here.
      showNotification(ACCOUNT_DENIED, 'Switching unavailable', 'error');
      break;
    default:
      reportError(new Error(`Workspace switch failed: ${error.code}`));
      showNotification(GENERIC_FAILURE, 'Switch failed', 'error');
  }
  return 'refused';
}

/**
 * Switches the AMS session to another workspace the caller holds a grant on.
 *
 * Goes through the generated AMS client rather than the session object. The switched token is
 * deliberately discarded: TenancySwitcher reloads the page on success, which throws away the
 * in-memory token store anyway, and the switch is durable server-side — 12a records the choice on
 * the caller's workspace grant, so the post-reload bootstrap lands on the switched workspace. Using
 * the client also means a 401 gets renew-and-retry from `fetchWithRenewal` instead of a full
 * redirect to the login page, which matters for a tab that slept past its renewal timer.
 *
 * The caller owns the reload — see TenancySwitcher.
 */
export function useWorkspaceSwitch(): UseWorkspaceSwitchReturn {
  const [isSwitching, setIsSwitching] = useState(false);

  const switchWorkspace = useCallback(async (workspaceCode: string): Promise<SwitchResult> => {
    setIsSwitching(true);
    try {
      const { error } = await amsPublicApiTokensSwitchWorkspace({ body: { workspace_code: workspaceCode } });
      return error ? refuse(error) : 'switched';
    } catch (err) {
      reportError(err);
      showNotification(GENERIC_FAILURE, 'Switch failed', 'error');
      return 'refused';
    } finally {
      setIsSwitching(false);
    }
  }, []);

  return { switchWorkspace, isSwitching };
}
